import { useField } from 'formik';

import { Question } from 'types/domainModels';
import { QuestionFormData } from 'types/forms';

import FormInput from 'components/common/forms/FormInput';
import FormSearchSelectInput from 'components/common/forms/FormSearchSelectInput';
import { ALLOCATION_UNIT_OPTIONS } from 'util/formOptions';
import CarryOverTotal from './CarryOverTotal';

interface AllocationSettingsProps {
  question: Question | undefined;
  questions: Question[];
}

const AllocationSettings = ({
  question,
  questions,
}: AllocationSettingsProps) => {
  const [{ value: allocationCarryOverTotalEnabled }] = useField<
    QuestionFormData['allocationCarryOverTotalEnabled']
  >('allocationCarryOverTotalEnabled');
  const [{ value: carryForward }] = useField<
    QuestionFormData['features']['carryForward']
  >('features.carryForward');

  const carryOverTotalEnabled = !!allocationCarryOverTotalEnabled;
  const maxPerOptionDisabled = carryOverTotalEnabled || !!carryForward?.enabled;

  return (
    <div className="py-6 border-b border-gray-300 space-y-4">
      <h3 className="text-base font-semibold text-gray-900 mb-4">
        Allocation Settings
      </h3>
      <CarryOverTotal question={question} questions={questions} />
      <div className="grid grid-cols-2 gap-4">
        <FormInput
          disabled={carryOverTotalEnabled}
          id="allocationTotal"
          label="Total to Allocate"
          labelFor="allocationTotal"
          name="allocationTotal"
          size="md"
          tooltip="The total amount that respondents must allocate across all options"
          type="number"
        />
        <FormSearchSelectInput
          inputId="allocationUnits"
          label="Unit Type"
          labelFor="allocationUnits"
          name="allocationUnits"
          options={ALLOCATION_UNIT_OPTIONS}
          tooltip="Select the unit type for allocation values"
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <FormInput
          id="allocationMinPerOption"
          label="Minimum Per Option"
          labelFor="allocationMinPerOption"
          name="allocationMinPerOption"
          placeholder="0"
          size="md"
          tooltip="Minimum amount that can be allocated to each option (default: 0)"
          type="number"
        />
        <FormInput
          disabled={maxPerOptionDisabled}
          id="allocationMaxPerOption"
          label="Maximum Per Option"
          labelFor="allocationMaxPerOption"
          name="allocationMaxPerOption"
          size="md"
          tooltip="Maximum amount that can be allocated to each option (default: total)"
          type="number"
        />
        <FormInput
          id="allocationStep"
          label="Step Size"
          labelFor="allocationStep"
          name="allocationStep"
          placeholder="1"
          size="md"
          tooltip="Increment for allocation values (default: 1)"
          type="number"
        />
      </div>
    </div>
  );
};

export default AllocationSettings;
